import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Waves, Droplets, Layers, ListChecks, ChevronUp, ChevronDown } from "lucide-react";
import Header from "@/components/Header";
import SimulatorPanel from "@/components/SimulatorPanel";
import ReadinessLedger from "@/components/ReadinessLedger";
import EdgeDrawer from "@/components/EdgeDrawer";
import LiveTicker from "@/components/LiveTicker";
import CopyrightFooter from "@/components/CopyrightFooter";
import { RoomHud, LedgerScene, StormScene, ROOM_SCENES, ROOM_SECONDS } from "@/components/CommandRoom";
import TacticalMap from "@/components/TacticalMap";
import TelemetryPanel from "@/components/TelemetryPanel";
import RainfallPanel from "@/components/RainfallPanel";
import AlertConsole from "@/components/AlertConsole";
import ThreatBanner from "@/components/ThreatBanner";
import HurricaneTracker from "@/components/HurricaneTracker";
import WindArrivalPanel from "@/components/WindArrivalPanel";
import BriefingPanel from "@/components/BriefingPanel";
import SatellitePanel from "@/components/SatellitePanel";
import AssetActionCard from "@/components/AssetActionCard";
import { ShelterPanel, ClosuresPanel } from "@/components/OpsPanels";
import { FieldAccessDialog, FieldBadge } from "@/components/FieldAccess";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { scoreAssets } from "@/lib/riskEngine";
import { RISK, ASSET_META, threatFor } from "@/lib/constants";
import {
  getAssets, getStatus, startIngest, exportUrl, getGullies, getInundation,
  getCascade, getTelemetryLive, getAlerts, clearAlerts, getForecast, getHurricanes,
  getClosures, getShelters, reopenClosure, getFieldSession, setFieldSession, formatApiError,
} from "@/lib/api";
import { Route, Anchor, Milestone, Hospital } from "lucide-react";

const TYPE_ICONS = { road: Route, port: Anchor, bridge: Milestone, shelter: Hospital };
const ALL_TIERS = ["critical", "high", "medium", "low"];
const ALL_TYPES = ["road", "port", "bridge", "shelter"];

function computeLedger(scored) {
  const tiers = { low: 0, medium: 0, high: 0, critical: 0 };
  const by_type = {};
  scored.forEach((s) => {
    tiers[s.tier]++;
    const bt = (by_type[s.asset_type] ||= { count: 0, exposed: 0, critical: 0 });
    bt.count++;
    if (s.tier !== "low") bt.exposed++;
    if (s.tier === "critical") bt.critical++;
  });
  const n = scored.length || 1;
  // readiness = share of assets NOT flooded or cut off right now
  return { total: scored.length, tiers, by_type, readiness: Math.round((1000 * (n - tiers.critical)) / n) / 10 };
}

export default function Dashboard() {
  const [raw, setRaw] = useState([]);
  const [scenario, setScenario] = useState("flooding");
  const [intensity, setIntensity] = useState(0);
  const [slr, setSlr] = useState(0);
  const [baseLayer, setBaseLayer] = useState("satellite");
  const [visibleTiers, setVisibleTiers] = useState(new Set(ALL_TIERS));
  const [visibleTypes, setVisibleTypes] = useState(new Set(ALL_TYPES));
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(true);

  // live engine state
  const [autonomous, setAutonomous] = useState(true);
  const [telemetry, setTelemetry] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [gullies, setGullies] = useState([]);
  const [inundation, setInundation] = useState(null);
  const [cascade, setCascade] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [webhookConfigured, setWebhookConfigured] = useState(false);
  const [showGullies, setShowGullies] = useState(false);
  const [satShown, setSatShown] = useState(null);
  const [leftOpen, setLeftOpen] = useState(false);
  const [rightOpen, setRightOpen] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(() => window.innerWidth >= 768);
  const [legendOpen, setLegendOpen] = useState(() => window.innerWidth >= 768);
  const [wide, setWide] = useState(() => window.innerWidth >= 640);
  useEffect(() => {
    const onResize = () => {
      setWide(window.innerWidth >= 640);
      if (window.innerWidth < 768) { setBannerOpen(false); setLegendOpen(false); }
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [vw, setVw] = useState(() => window.innerWidth);
  useEffect(() => {
    const f = () => setVw(window.innerWidth);
    window.addEventListener("resize", f);
    return () => window.removeEventListener("resize", f);
  }, []);
  const bannerShown = bannerOpen && !(leftOpen && rightOpen && vw < 1700);
  const overlayTop = bannerShown ? 172 : 58;
  const shiftCtl = wide && rightOpen && bannerShown;
  const [room, setRoom] = useState(false);
  const [sceneIdx, setSceneIdx] = useState(0);
  const [roomLeft, setRoomLeft] = useState(ROOM_SECONDS);
  const wasFs = useRef(false);
  const enterRoom = useCallback(() => {
    setLeftOpen(false); setRightOpen(false); setSelectedId(null);
    setSceneIdx(0); setRoomLeft(ROOM_SECONDS); setRoom(true);
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().then(() => { wasFs.current = true; }).catch(() => {});
  }, []);
  const exitRoom = useCallback(() => {
    setRoom(false);
    wasFs.current = false;
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
  }, []);
  useEffect(() => {
    if (!room) return undefined;
    const tick = setInterval(() => setRoomLeft((v) => {
      if (v > 1) return v - 1;
      setSceneIdx((i) => (i + 1) % ROOM_SCENES.length);
      return ROOM_SECONDS;
    }), 1000);
    const onKey = (e) => { if (e.key === "Escape") exitRoom(); };
    const onFs = () => { if (!document.fullscreenElement && wasFs.current) exitRoom(); };
    window.addEventListener("keydown", onKey);
    document.addEventListener("fullscreenchange", onFs);
    return () => { clearInterval(tick); window.removeEventListener("keydown", onKey); document.removeEventListener("fullscreenchange", onFs); };
  }, [room, exitRoom]);
  const [showInundation, setShowInundation] = useState(true);

  // forecast time-machine + hurricanes
  const [forecast, setForecast] = useState(null);
  const [forecastHour, setForecastHour] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const [drill, setDrill] = useState(null);
  const [forecastActive, setForecastActive] = useState(false);
  const [hurricanes, setHurricanes] = useState(null);

  const [hazardTick, setHazardTick] = useState(0);
  const [session, setSession] = useState(getFieldSession());
  const [fieldOpen, setFieldOpen] = useState(false);
  const [closures, setClosures] = useState([]);
  const [shelters, setShelters] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [rightTab, setRightTab] = useState("ledger");
  const pollRef = useRef(null);
  const simRef = useRef(null);
  const teleRef = useRef(null);

  const loadAssets = useCallback(async () => {
    try {
      setRaw(await getAssets());
    } catch (e) {
      toast.error("Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshClosures = useCallback(async () => {
    try { setClosures((await getClosures()).closures || []); } catch (e) { /* noop */ }
  }, []);

  const refreshAlerts = useCallback(async () => {
    try {
      const d = await getAlerts();
      setAlerts(d.alerts || []);
      setWebhookConfigured(!!d.webhook_configured);
    } catch (e) { /* noop */ }
  }, []);

  // initial load
  useEffect(() => {
    (async () => {
      const s = await getStatus().catch(() => null);
      setStatus(s);
      if (s?.status === "running") startPolling();
      await loadAssets();
      getGullies().then(setGullies).catch(() => {});
      getForecast().then(setForecast).catch(() => {});
      getHurricanes().then(setHurricanes).catch(() => {});
      refreshAlerts();
      refreshClosures();
    })();
    return () => { clearInterval(pollRef.current); clearTimeout(simRef.current); clearInterval(teleRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // forecast playback loop
  useEffect(() => {
    if (!playing || !forecast) return;
    const id = setInterval(() => {
      setForecastHour((h) => {
        const nh = h + 1;
        if (nh >= forecast.hours.length) { setPlaying(false); return h; }
        return nh;
      });
    }, 450 / speed);
    return () => clearInterval(id);
  }, [playing, speed, forecast]);

  // apply the active forecast hour onto the live simulator (drives map + ledger)
  useEffect(() => {
    if (!forecastActive || !forecast) return;
    const hr = forecast.hours[forecastHour];
    if (!hr) return;
    setScenario("flooding");
    setIntensity(hr.intensity);
    setSlr(Math.max(-1, Math.min(8, hr.water_level_m - hr.intensity * 4)));
  }, [forecastHour, forecastActive, forecast]);

  const enterForecast = () => { setForecastActive(true); setAutonomous(false); };
  const handleScrub = (h) => { enterForecast(); setForecastHour(h); };
  const handlePlayPause = () => { enterForecast(); setPlaying((p) => !p); };
  const handleDrill = async (cat) => {
    try {
      const fc = await getForecast(cat);
      setForecast(fc); setDrill(cat); setForecastHour(0);
      enterForecast(); setPlaying(true);
      toast.warning(`DRILL: simulating ${cat.toUpperCase()} landfall — this is a training scenario, not a forecast`);
    } catch (e) { toast.error("Could not load drill"); }
  };
  const handleReplay = async (key) => {
    try {
      toast.info("Loading historical replay — NOAA best track + ERA5 weather…");
      const fc = await getForecast(key);
      setForecast(fc); setDrill(key); setForecastHour(0);
      enterForecast(); setPlaying(true);
      toast.success(`REPLAY: ${fc.replay_meta?.name} ${fc.replay_meta?.year} — what the engine would have shown`);
    } catch (e) { toast.error("Could not load replay"); }
  };
  const handleLive = async () => {
    try {
      const fc = await getForecast();
      setForecast(fc); setDrill(null); setForecastHour(0); setPlaying(false);
    } catch (e) { /* noop */ }
  };

  // autonomous telemetry loop
  const syncTelemetry = useCallback(async () => {
    try {
      const t = await getTelemetryLive();
      setTelemetry(t);
      setLastSync(new Date().toLocaleTimeString());
      if (autonomous && t?.autonomous) {
        setScenario(t.autonomous.scenario);
        setIntensity(t.autonomous.intensity);
        setSlr(t.autonomous.sea_level_rise);
      }
      refreshAlerts();
    } catch (e) { /* noop */ }
  }, [autonomous, refreshAlerts]);

  useEffect(() => {
    clearInterval(teleRef.current);
    syncTelemetry();
    teleRef.current = setInterval(syncTelemetry, 60000);
    return () => clearInterval(teleRef.current);
  }, [syncTelemetry]);

  // throttled inundation + cascade + gully fusion whenever the hazard changes
  const hazardArgs = useRef(null);
  useEffect(() => {
    if (!raw.length) return;
    hazardArgs.current = [scenario, intensity, slr, forecastActive ? forecastHour : 0, forecastActive ? drill : null];
    if (simRef.current) return;
    simRef.current = setTimeout(async () => {
      const args = hazardArgs.current;
      try {
        const [sc, it, sl, hh, dd] = args;
        const [inu, cas, shl] = await Promise.all([getInundation(...args), getCascade(...args),
          getShelters({ scenario: sc, intensity: it, sea_level_rise: sl, hour: hh, drill: dd }).catch(() => null)]);
        setInundation(inu);
        setCascade(cas);
        if (shl) setShelters(shl);
      } catch (e) { /* DEM may be missing */ }
      simRef.current = null;
      if (hazardArgs.current !== args) setHazardTick((t) => t + 1);
    }, 350);
  }, [scenario, intensity, slr, raw.length, forecastActive, forecastHour, drill, hazardTick]);

  const startPolling = () => {
    setSyncing(true);
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const s = await getStatus().catch(() => null);
      setStatus(s);
      if (s && s.status !== "running") {
        clearInterval(pollRef.current);
        setSyncing(false);
        if (s.status === "complete") {
          const src = Object.values(s.counts?.osm_source || {}).includes("osm_extract") ? " (OSM offline extract — Overpass busy)" : "";
          toast.success(`Ingestion complete · ${s.total} assets${src}`); loadAssets();
        }
        else if (s.status === "error" || s.status === "interrupted") toast.error(s.message || "Ingestion failed");
      }
    }, 2000);
  };

  const handleSync = async () => {
    try {
      await startIngest();
      toast.info("Satellite ingestion started");
      startPolling();
    } catch (e) { toast.error("Could not start ingestion"); }
  };

  const handleExport = () => window.open(exportUrl(scenario, intensity, slr), "_blank");
  const handleClearAlerts = async () => {
    try { await clearAlerts(); refreshAlerts(); toast.success("Alerts cleared"); }
    catch (e) { toast.error("Sign in (field team or admin) to clear alerts"); }
  };

  const scored = useMemo(() => scoreAssets(raw, inundation, inundation?.water_level_m ?? (slr + intensity * 4)),
    [raw, inundation, slr, intensity]);
  const ledger = useMemo(() => computeLedger(scored), [scored]);
  const isolatedIds = useMemo(() => new Set(cascade?.isolated_shelters || []), [cascade]);

  const waterLevel = Math.round((slr + intensity * 4) * 100) / 100;
  const liveIsolated = cascade?.isolated_count || 0;
  const hr = forecastActive && forecast?.hours?.[forecastHour];
  const gullyCounts = inundation?.runoff?.counts;
  const liveOverflow = (gullyCounts?.overflow || 0) + (gullyCounts?.surcharge || 0);
  const currentThreat = hr ? hr.threat : (cascade?.threat || "CLEAR");
  const currentMetrics = hr
    ? { water_level_m: hr.water_level_m, flooded_count: hr.flooded_count, isolated_count: hr.isolated_count }
    : { water_level_m: waterLevel, flooded_count: inundation?.flagged_count || 0, isolated_count: liveIsolated };

  const handleAutonomous = (on) => {
    setAutonomous(on);
    if (on) { setForecastActive(false); setPlaying(false); }
  };

  const storm = forecast?.storm;
  const stormActive = forecastActive && !!storm;
  let countdown = null;
  if (stormActive) {
    const d = storm.landfall_hour - forecastHour;
    const lbl = forecast?.replay ? "CLOSEST APPROACH" : "LANDFALL";
    countdown = d > 0 ? `${lbl} +${d}h` : d === 0 ? `${lbl} NOW` : "MOVING AWAY";
  }

  const rainFlagged = useMemo(() => new Set(Object.keys(inundation?.rain_flagged || {})), [inundation]);
  const frameTarget = useMemo(() => {
    if (stormActive) {
      return { key: `drill-${drill}-${forecast?.generated_at}`, label: storm.name, auto: true,
        points: [...storm.track.map((p) => [p.lat, p.lon]), ...(storm.cone?.[0] || [])] };
    }
    const threat = hurricanes?.barbados_threat;
    const s = threat || (hurricanes?.storms || []).find((x) => x.in_atlantic);
    if (!s) return null;
    return { key: `nhc-${s.id}-${s.advisory || ""}`, label: s.name, auto: !!threat,
      points: [s.position, ...(s.forecast_points || []).map((p) => [p.lat, p.lon]), ...(s.cone?.[0] || [])] };
  }, [stormActive, storm, drill, hurricanes, forecast?.generated_at]);

  const closedIds = useMemo(() => new Set(closures.map((c) => c.asset_id)), [closures]);
  const selected = useMemo(() => scored.find((a) => a.id === selectedId) || null, [scored, selectedId]);
  const handleSelect = useCallback((a) => {
    setSelectedId(a.id);
    if (window.innerWidth < 640) { setLeftOpen(false); setRightOpen(false); }
  }, []);
  const onFieldChanged = () => { refreshClosures(); setHazardTick((t) => t + 1); };
  const handleReopen = async (id) => {
    try { await reopenClosure(id); toast.success("Reopened — isolation model updated"); onFieldChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const signOut = () => { setFieldSession(null); setSession(null); toast.info("Field mode ended"); };

  const toggle = (setter) => (key) =>
    setter((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); return n; });
  const toggleTier = toggle(setVisibleTiers);
  const toggleType = toggle(setVisibleTypes);

  const empty = !loading && raw.length === 0;
  const roomLocked = room && !!hurricanes?.barbados_threat;
  const scene = roomLocked ? "storms" : ROOM_SCENES[sceneIdx].id;
  const tm = telemetry?.model;
  const atl = (hurricanes?.storms || []).filter((x) => x.in_atlantic);
  const tc = { CLEAR: "#22C55E", WATCH: "#EAB308", WARNING: "#FF9500", SEVERE: "#FF3B30", CRITICAL: "#FF3B30" };
  const tickerItems = [
    { label: "Threat", value: `${currentThreat}${forecastActive ? ` · T+${forecastHour}h` : ""}`, color: tc[currentThreat] },
    tm && { label: "Sea level", value: `${tm.still_water_level_m?.toFixed(2)} m vs high-tide line ${tm.mhhw_m?.toFixed(2)} m`,
      color: tm.still_water_level_m > tm.mhhw_m ? "#FF9500" : "#22C55E" },
    tm && { label: "Tide", value: `${tm.astronomical_tide_m?.toFixed(2)} m (Bridgetown harmonic)` },
    tm && { label: "Surge", value: `${tm.surge_m >= 0 ? "+" : ""}${tm.surge_m?.toFixed(2)} m` },
    telemetry?.atmos && { label: "Wind", value: `${Math.round(telemetry.atmos.wind_speed_kmh)} km/h from ${telemetry.atmos.wind_direction_deg}°` },
    telemetry?.atmos && { label: "Pressure", value: `${telemetry.atmos.pressure_hpa} hPa` },
    telemetry?.atmos && { label: "Rain", value: `${telemetry.atmos.precip_mm} mm/h`, color: telemetry.atmos.precip_mm > 10 ? "#FF9500" : undefined },
    inundation?.waves && { label: "Waves", value: `${inundation.waves.hs_m} m · run-up ≤ ${inundation.waves.shoreline_runup_max_m} m` },
    ledger && { label: "Red zones", value: ledger.tiers?.critical ?? 0, color: (ledger.tiers?.critical || 0) ? "#FF3B30" : "#22C55E" },
    ledger && { label: "Amber", value: ledger.tiers?.high ?? 0, color: (ledger.tiers?.high || 0) ? "#FF9500" : "#22C55E" },
    { label: "Alerts", value: alerts.length, color: alerts.length ? "#FF3B30" : "#22C55E" },
    shelters?.summary && { label: "Shelters", value: `${shelters.summary.open}/${shelters.summary.public_shelters} open · ${shelters.summary.usable_capacity} places` },
    { label: "Road closures", value: closures.length, color: closures.length ? "#FF9500" : undefined },
    { label: "Atlantic storms", value: atl.length ? atl.map((x) => `${x.name} ${Math.round(x.distance_km)} km`).join(", ") : "none active",
      color: atl.some((x) => x.approaching) ? "#FF3B30" : undefined },
  ];

  return (
    <div data-testid="app-root" className={`h-screen w-screen flex flex-col overflow-hidden bg-[#020b24] tactical-grid ${room ? "room-mode" : ""}`}>
      {!room && <Header ledger={ledger} status={status} onSync={handleSync} syncing={syncing} cascade={cascade} onCommandRoom={enterRoom} />}
      {room && <RoomHud scene={scene} left={roomLeft} locked={roomLocked} threat={currentThreat} threatColor={tc[currentThreat]} onExit={exitRoom} storm={hurricanes?.barbados_threat} />}
      {room && scene === "ledger" && <LedgerScene ledger={ledger} readiness={ledger?.readiness ?? 0} isolated={cascade?.isolated_count ?? 0} assets={scored} />}
      {room && scene === "storms" && <StormScene storms={hurricanes?.storms} threat={hurricanes?.barbados_threat} />}

      <div className="relative flex-1 min-h-0 overflow-hidden">
        <main data-testid="map-stage" className="absolute inset-0 isolate z-0">
          {empty ? (
            <EmptyState onSync={handleSync} syncing={syncing} status={status} />
          ) : (
            <TacticalMap
              assets={scored} visibleTiers={visibleTiers} visibleTypes={visibleTypes}
              onSelect={handleSelect} baseLayer={baseLayer} closedIds={closedIds}
              gullies={gullies} inundation={inundation}
              showGullies={showGullies} showInundation={showInundation}
              isolatedIds={isolatedIds}
              storm={storm} stormHour={forecastHour} stormActive={stormActive}
              gullySegments={inundation?.gully_segments} rainFlagged={rainFlagged}
              nhcStorms={hurricanes?.storms} frameTarget={frameTarget} satellite={satShown}
              controlsRight={wide && rightOpen ? 452 : 12} controlsTop={shiftCtl ? 172 : 12}
              roomView={room ? (scene === "storms" ? "storm" : "island") : null}
            />
          )}

          <div className="absolute inset-0 z-[460] pointer-events-none hud-vignette" />
          <div style={{ right: wide && rightOpen ? 488 : 48, top: shiftCtl ? 264 : 104 }} className="room-hide absolute z-[550] transition-[right,top] duration-300">
            <FieldBadge session={session} onSignIn={() => setFieldOpen(true)} onSignOut={signOut} />
          </div>
          {!room && <AssetActionCard asset={selected} session={session}
            closure={closures.find((c) => c.asset_id === selectedId)}
            shelter={shelters?.shelters?.find((r) => r.asset_id === selectedId)}
            onClose={() => setSelectedId(null)} onChanged={onFieldChanged} onRequireLogin={() => setFieldOpen(true)}
            rightOffset={wide && rightOpen ? 440 : 0} />}
          {forecastActive && forecast?.replay_meta && (
            <div data-testid="replay-info-card" style={{ top: overlayTop, left: wide && leftOpen ? 488 : 48 }} className="absolute z-[590] glass rounded-md px-3 py-1.5 border border-amber-400/50 font-mono-t text-[10px] text-amber-200 max-w-[300px] leading-relaxed">
              HISTORICAL REPLAY · {forecast.replay_meta.name} {forecast.replay_meta.year} · closest approach {forecast.replay_meta.closest_approach_km} km
              @ {forecast.replay_meta.wind_at_closest_kt} kt · ERA5 rain {forecast.replay_meta.era5_rain_72h_mm} mm/72h · ERA5 peak wave {forecast.replay_meta.era5_peak_wave_m != null ? `${forecast.replay_meta.era5_peak_wave_m} m` : (forecast.replay_meta.wave_status === "pending" ? "loading from Copernicus…" : "unavailable")} · engine peak: <span className="font-bold">{forecast.peak?.threat}</span>
            </div>
          )}
          {countdown && (
            <div data-testid="landfall-countdown"
              style={{ top: overlayTop }} className="absolute left-1/2 -translate-x-1/2 z-[600] glass rounded-md px-4 py-1.5 border border-risk-critical/60 flex items-center gap-2 risk-critical">
              <span className="w-2 h-2 rounded-full bg-risk-critical" />
              <span className="font-heading font-bold uppercase tracking-wide text-sm text-risk-critical">
                {storm?.name} · {countdown}
              </span>
            </div>
          )}

          {!room && !legendOpen && (
            <button data-testid="legend-expand-button" onClick={() => setLegendOpen(true)}
              className="absolute bottom-3 left-12 z-[500] glass rounded-full px-3 py-1.5 border border-cyan/30 flex items-center gap-1.5 font-mono-t text-[10px] uppercase text-cyan">
              <Layers className="w-3.5 h-3.5" /> Layers
            </button>
          )}
          <div data-testid="map-legend" className={`room-hide absolute bottom-3 left-12 z-[500] max-w-[calc(100vw-7rem)] glass rounded-xl p-3 space-y-2 shadow-2xl ${legendOpen ? "" : "hidden"}`}>
            <button data-testid="legend-collapse-button" onClick={() => setLegendOpen(false)}
              className="absolute top-2 right-2 text-slate-400 hover:text-cyan"><ChevronDown className="w-3.5 h-3.5" /></button>
            <div className="font-mono-t text-[9px] uppercase tracking-widest text-slate-400">Risk Tiers</div>
            <div className="flex gap-3">
              {ALL_TIERS.map((t) => (
                <div key={t} className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: RISK[t].color }} />
                  <span className="font-mono-t text-[10px] text-slate-300">{RISK[t].label}</span>
                </div>
              ))}
            </div>
            <div className="h-px bg-border my-1" />
            <div className="flex gap-1.5 flex-wrap max-w-[260px]">
              {ALL_TYPES.map((t) => {
                const Icon = TYPE_ICONS[t];
                const active = visibleTypes.has(t);
                return (
                  <button key={t} data-testid={`layer-toggle-${t}`} onClick={() => toggleType(t)}
                    className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-mono-t uppercase tracking-wide transition-all ${
                      active ? "bg-cyan/10 border-cyan/50 text-cyan" : "bg-slate-900/60 border-border text-slate-500"}`}>
                    <Icon className="w-3 h-3" /> {ASSET_META[t].label.split(" ")[0]}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-1.5 flex-wrap max-w-[260px]">
              <button data-testid="layer-toggle-inundation" onClick={() => setShowInundation((v) => !v)}
                className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-mono-t uppercase tracking-wide transition-all ${
                  showInundation ? "bg-sky-500/15 border-sky-400/60 text-sky-300" : "bg-slate-900/60 border-border text-slate-500"}`}>
                <Droplets className="w-3 h-3" /> Flood
              </button>
              <button data-testid="layer-toggle-gullies" onClick={() => setShowGullies((v) => !v)}
                className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] font-mono-t uppercase tracking-wide transition-all ${
                  showGullies ? "bg-sky-500/15 border-sky-400/60 text-sky-300" : "bg-slate-900/60 border-border text-slate-500"}`}>
                <Waves className="w-3 h-3" /> Gullies
              </button>
            </div>
            {cascade && (
              <div className="font-mono-t text-[10px] text-slate-400 pt-1">
                Isolated shelters: <span className="text-risk-critical font-bold">{cascade.isolated_count}</span> · Inundated: <span className="text-sky-300 font-bold">{inundation?.coastal_flagged_count ?? 0}</span> · Gully: <span data-testid="map-gully-flagged-count" className="text-orange-400 font-bold">{inundation?.rain_flagged_count ?? 0}</span> · Waves: <span data-testid="map-wave-flagged-count" className="text-cyan font-bold">{inundation?.wave_flagged_count ?? 0}</span>
                {inundation?.waves && <div data-testid="map-wave-summary" className="text-[9px] text-slate-500">ECMWF WAM forecast: Hs {inundation.waves.hs_m} m · Tp {inundation.waves.tp_s} s · from {Math.round(inundation.waves.dir_deg)}° · max run-up {inundation.waves.shoreline_runup_max_m} m</div>}
              </div>
            )}
          </div>
        </main>

        {room ? null : bannerShown ? (
          <div data-testid="floating-threat-banner" style={wide ? { left: leftOpen ? 488 : 48, right: rightOpen ? 488 : 190 } : undefined}
            className="absolute top-3 left-12 right-12 z-[600] mx-auto rounded-2xl transition-[left,right] duration-[380ms] overflow-hidden shadow-[0_12px_40px_rgba(0,0,0,0.55)] border border-cyan/20">
            <button data-testid="banner-collapse-button" onClick={() => setBannerOpen(false)} title="Collapse"
              className="absolute top-1.5 right-2 z-10 text-slate-400 hover:text-cyan"><ChevronUp className="w-4 h-4" /></button>
        <ThreatBanner
          threat={currentThreat}
          peak={forecast?.peak}
          hours={forecast?.hours}
          forecastHour={forecastHour}
          playing={playing}
          onPlayPause={handlePlayPause}
          onScrub={handleScrub}
          speed={speed}
          onSpeed={setSpeed}
          drill={drill}
          onDrill={handleDrill}
          onReplay={handleReplay}
          onLive={handleLive}
          source={forecast?.source}
          current={currentMetrics}
          countdown={countdown}
        />

          </div>
        ) : (
          <button data-testid="banner-expand-button" onClick={() => setBannerOpen(true)}
            className="absolute top-3 left-1/2 -translate-x-1/2 z-[600] glass rounded-full px-4 py-1.5 border flex items-center gap-2 shadow-xl"
            style={{ borderColor: `${tc[currentThreat]}88` }}>
            <span className="w-2 h-2 rounded-full pulse-dot" style={{ background: tc[currentThreat] }} />
            <span className="font-heading font-bold uppercase tracking-wide text-sm" style={{ color: tc[currentThreat] }}>{currentThreat}</span>
            <ChevronDown className="w-4 h-4 text-slate-300" />
          </button>
        )}

        <EdgeDrawer side="left" open={leftOpen} onToggle={() => setLeftOpen((v) => !v)} label="Intel & tools"
          Icon={Layers} testid="left-drawer" otherOpen={rightOpen}
          dot={hurricanes?.storms?.some((x) => x.in_atlantic && x.approaching) ? "#FF3B30" : "#22C55E"}>
          <HurricaneTracker data={hurricanes} />
          <WindArrivalPanel wind={stormActive ? storm.wind_arrival : hurricanes?.barbados_wind} drillHour={forecastHour} />
          <TelemetryPanel telemetry={telemetry} autonomous={autonomous} setAutonomous={handleAutonomous} lastSync={lastSync} />
          <RainfallPanel rainfall={forecast?.rainfall} currentPrecip={telemetry?.atmos?.precip_mm} forecastHour={forecastHour} active={forecastActive} runoff={inundation?.runoff} />
          <SimulatorPanel
            scenario={scenario} setScenario={setScenario}
            intensity={intensity} setIntensity={setIntensity}
            slr={slr} setSlr={setSlr}
            baseLayer={baseLayer} setBaseLayer={setBaseLayer}
            onExport={handleExport}
          />
          <BriefingPanel params={{ scenario, intensity, sea_level_rise: slr,
            hour: forecastActive ? forecastHour : 0, drill: forecastActive ? drill : null }} />
          <SatellitePanel onShow={setSatShown} shownId={satShown?.id} onSelect={(a) => setSelectedId(a.id)} />
        </EdgeDrawer>

        <EdgeDrawer side="right" open={rightOpen} onToggle={() => setRightOpen((v) => !v)} label="Ledger & alerts"
          Icon={ListChecks} testid="right-drawer" otherOpen={leftOpen} fill
          dot={(ledger?.tiers?.critical || 0) > 0 ? "#FF3B30" : (ledger?.tiers?.high || 0) > 0 ? "#FF9500" : "#22C55E"}>
          <div className="flex flex-col gap-3 flex-1 min-h-0">
          <Tabs value={rightTab} onValueChange={setRightTab} className="flex-1 min-h-0 flex flex-col">
            <TabsList className="glass grid grid-cols-3 h-8 shrink-0">
              <TabsTrigger data-testid="tab-ledger" value="ledger" className="font-mono-t text-[10px] uppercase">Ledger</TabsTrigger>
              <TabsTrigger data-testid="tab-shelters" value="shelters" className="font-mono-t text-[10px] uppercase">Shelters</TabsTrigger>
              <TabsTrigger data-testid="tab-closures" value="closures" className="font-mono-t text-[10px] uppercase">
                Closures{closures.length ? ` (${closures.length})` : ""}
              </TabsTrigger>
            </TabsList>
            <TabsContent value="ledger" className="flex-1 min-h-0 mt-2">
              <ReadinessLedger
                ledger={ledger}
                assets={scored.filter((a) => visibleTiers.has(a.tier) && visibleTypes.has(a.asset_type))}
                visibleTiers={visibleTiers} toggleTier={toggleTier} onSelect={handleSelect}
                inundation={inundation}
              />
            </TabsContent>
            <TabsContent value="shelters" className="flex-1 min-h-0 mt-2 glass rounded-lg overflow-hidden">
              <ShelterPanel data={shelters} onSelect={setSelectedId} />
            </TabsContent>
            <TabsContent value="closures" className="flex-1 min-h-0 mt-2 glass rounded-lg overflow-hidden">
              <ClosuresPanel closures={closures} session={session} onReopen={handleReopen} onSelect={setSelectedId} />
            </TabsContent>
          </Tabs>
          <AlertConsole alerts={alerts} onClear={handleClearAlerts} webhookConfigured={webhookConfigured} onRoutingChange={() => getAlerts().then((d) => setWebhookConfigured(!!d.webhook_configured)).catch(() => {})} />
          </div>
        </EdgeDrawer>
      </div>

      <LiveTicker items={tickerItems} />
      <CopyrightFooter />
      <FieldAccessDialog open={fieldOpen} onOpenChange={setFieldOpen} onSession={setSession} />
    </div>
  );
}

function EmptyState({ onSync, syncing, status }) {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 tactical-grid bg-[#050810]">
      <div className="w-16 h-16 rounded-lg border border-cyan/40 bg-cyan/5 flex items-center justify-center mb-5 pulse-glow">
        {syncing ? <Loader2 className="w-7 h-7 text-cyan animate-spin" /> : <span className="text-cyan text-3xl">◎</span>}
      </div>
      <h2 className="font-heading font-bold uppercase tracking-wide text-xl text-slate-100">No Infrastructure Mapped</h2>
      <p className="font-mono-t text-xs text-slate-500 mt-2 max-w-sm leading-relaxed">
        {syncing ? status?.message || "Ingesting live satellite + OpenStreetMap data..."
                 : "Run satellite ingestion to pull the live Barbados network from OpenStreetMap and the elevation DEM."}
      </p>
      {!syncing && (
        <button data-testid="empty-sync-button" onClick={onSync}
          className="mt-5 bg-cyan text-slate-950 font-heading font-semibold uppercase tracking-wide text-sm px-6 py-2.5 rounded-md hover:bg-cyan/85 transition-all">
          Ingest Barbados Network
        </button>
      )}
    </div>
  );
}
